import React, { useEffect, useMemo } from 'react';
import { Button, Select } from '@mantine/core';
import { useForm } from '@mantine/form';

import { useMutation } from '@apollo/client';
import { Manager, ProcurementExecutive } from '../../codegen/graphql';
import { LINK_PROCUREMENT_MANAGER } from '../../apollo/mutations/procurementManager';
import { showErrorNotification } from '../../notifications';
import { GET_PROCUREMENT_MANAGERS } from '../../apollo/queries/procurementManager';

type Props = {
    procurementExecutiveId?: string | undefined;
    executives?: ProcurementExecutive[];
    close: () => void;
};

type SelectOption = { value: string; label: string };

function AddProcurementManagerForm({
    procurementExecutiveId,
    executives,
    close,
}: Props) {
    const allProcurementManagers: Manager[] = useMemo(() => {
        if (executives) {
            let managers: Manager[] = [];
            executives.forEach(exec => {
                managers = managers.concat(exec.managers ?? []);
            });
        }
        return [];
    }, []);

    const [
        linkProcurementManager,
        {
            data: linkProcurementManagerData,
            loading: linkProcurementManagerLoading,
            error: linkProcurementManagerError,
        },
    ] = useMutation(LINK_PROCUREMENT_MANAGER, {
        refetchQueries: [GET_PROCUREMENT_MANAGERS],
        awaitRefetchQueries: true,
    });

    const form = useForm({
        initialValues: {
            procurementManagerId: '',
            procurementExecutiveId: procurementExecutiveId || '',
        },
        validate: {
            procurementManagerId: value =>
                !value ? 'Procurement Manager is required' : null,
            procurementExecutiveId: value =>
                !value ? 'Procurement Executive is required' : null,
        },
    });

    const procurementManagers = useMemo(() => {
        if (allProcurementManagers) {
            return allProcurementManagers.map(manager => ({
                value: manager.id,
                label: `${manager.firstName} ${manager.lastName}`,
            }));
        }
        return [];
    }, [allProcurementManagers]);

    const executiveOptions: SelectOption[] = useMemo(() => {
        if (executives) {
            return executives.map(executive => ({
                value: executive.id ?? '',
                label: `${executive.firstName} ${executive.lastName}`,
            }));
        }
        return [];
    }, [executives]);

    const handleSubmit = form.onSubmit(values => {
        linkProcurementManager({
            variables: {
                input: {
                    managerId: values.procurementManagerId,
                    executiveId: values.procurementExecutiveId,
                },
            },
        });
    });

    useEffect(() => {
        if (linkProcurementManagerData) {
            form.reset();
            close();
        }
        if (linkProcurementManagerError) {
            showErrorNotification('Error linking procurement manager');
        }
    }, [linkProcurementManagerError, linkProcurementManagerData]);

    return (
        <form onSubmit={handleSubmit} className="p-4">
            <Select
                label="Select Procurement Manager"
                placeholder="Procurement Manager"
                searchable
                data={procurementManagers as SelectOption[]}
                {...form.getInputProps('procurementManagerId')}
            />
            {!procurementExecutiveId && (
                <Select
                    label="Select Procurement Executive"
                    placeholder="Procurement Executive"
                    data={executiveOptions}
                    {...form.getInputProps('procurementExecutiveId')}
                    className="mt-5"
                />
            )}
            <div className="flex mt-8 gap-2">
                <Button size="md" variant="outline" onClick={close}>
                    Cancel
                </Button>
                <Button
                    size="md"
                    type="submit"
                    loading={linkProcurementManagerLoading}
                >
                    Submit
                </Button>
            </div>
        </form>
    );
}

export default AddProcurementManagerForm;
